import { INode } from 'shared/types/tree'
import { nodeForId, nodesOfTypeWithContent } from '../../utility/TreeHelpers'
import { parseHtmlString } from 'shared/utils'
import { contentFromNode } from '../htmlContentUpdates/utility'
import { NodeIdMap } from '../../version_mapping/utility'
import { NavigableNodeTypes, NodeType } from 'shared/types/ingestion'
import { NodeParamsData, UpdateNodeData } from '../nodeUpdates/types'
import {
  doUpdate,
  idsForNode,
  UpdateMethod,
  UpdateType,
} from '../nodeUpdates/utility'
import { NodePathItem } from '../../contentService/types'

export function nodesWithImgElements(tree: INode) {
  const extraCheck = (node: INode) => !!getImgElementsForNode(node).length
  return nodesOfTypeWithContent({
    tree,
    content: ['<img'],
    types: [],
    extraCheck,
  })
}

export function getImgElementsForNode(node: INode) {
  return getElementsOfType(node, 'img')
}

function getElementsOfType(node: INode, elmType: string) {
  const doc = parseHtmlString(contentFromNode(node, false))
  return Array.from(doc.getElementsByTagName(elmType))
}

export function nodePath(
  node: { id: string; type: string; parent_id?: string },
  idMap: NodeIdMap,
) {
  const filtered = [NodeType.Inline, NodeType.NodeList]
  let currentNode = node
  const path = NavigableNodeTypes.includes(node.type as NodeType)
    ? [{ type: node.type, nid: node.id }]
    : []
  while (currentNode.parent_id) {
    currentNode = idMap[currentNode.parent_id].node
    if (!filtered.includes(currentNode.type as NodeType)) {
      path.push({ type: currentNode.type, nid: currentNode.id })
    }
  }
  return path.reverse()
}

export function allNodePathsById(
  nodes: { id: string; type: string; parent_id?: string }[],
  idMap: NodeIdMap,
) {
  return nodes.reduce<{
    paths: Record<string, { type: string; nid: string }[]>
    maxPath: number
  }>(
    ({ paths, maxPath }, node) => {
      const path = nodePath(node, idMap)
      return {
        paths: {
          ...paths,
          [node.id]: path,
        },
        maxPath: Math.max(maxPath, path.length),
      }
    },
    { paths: {}, maxPath: 0 },
  )
}

async function addNodeListType(type: string, nodeList: INode) {
  const typeUpdate: UpdateNodeData = {
    type: UpdateType.node,
    ids: idsForNode(nodeList),
    method: UpdateMethod.update,
    params: {
      body: {
        ...nodeList.body,
        type,
      },
    },
  }
  return doUpdate(typeUpdate)
}

export async function updateNodeListType(nodeList: INode) {
  if (!nodeListNeedsTypeUpdate(nodeList)) return
  const type = nodeList.nodes[0].type
  return addNodeListType(type, nodeList)
}

export function nodeListNeedsTypeUpdate(node: INode) {
  if (node.type !== NodeType.NodeList) return false
  return !node.body?.type
}

export async function createNewNode(
  parentNode: INode,
  nodeData: NodeParamsData,
  next_sibling_node_id: string | undefined,
) {
  const updateData: UpdateNodeData = {
    type: UpdateType.node,
    ids: idsForNode(parentNode),
    method: UpdateMethod.create,
    params: {
      ...nodeData,
      next_sibling_node_id,
    },
  }
  await doUpdate(updateData)
  if (nodeListNeedsTypeUpdate(parentNode)) {
    await addNodeListType(nodeData.type!, parentNode)
  }
}

export function getAncestorInTreeForCondition(
  node: INode,
  condition: (n: INode) => boolean,
  fullTree: INode,
) {
  let currentNode: INode | undefined = node
  while (currentNode && !condition(currentNode)) {
    if (currentNode.parent_id) {
      currentNode = nodeForId(currentNode.parent_id, fullTree)
    } else {
      currentNode = undefined
    }
  }
  return currentNode
}

export function nodePathString(nodePath: NodePathItem[] | undefined) {
  if (!nodePath) return ''

  const filteredTypes: NodeType[] = [NodeType.Course, NodeType.NodeList]
  return nodePath
    .filter((item) => {
      return !filteredTypes.includes(item.type)
    })
    .map((item) => item.title)
    .join(' > ')
}
